import type { DefaultMantineColor } from '@mantine/core';
/**
 * Single source of truth for Mantine color system constants.
 *
 * All color-related logic should import from this module.
 */

/**
 * Available Mantine theme color names.
 */
export const MantineThemeColors = [
  'blue',
  'red',
  'green',
  'pink',
  'grape',
  'violet',
  'indigo',
  'cyan',
  'teal',
  'lime',
  'yellow',
  'orange',
  'dark',
] as const satisfies DefaultMantineColor[];

export type MantineColor = (typeof MantineThemeColors)[number];

/**
 * Default shade used for chart series and other auto-colored elements.
 * Shade 6 is the primary shade in Mantine's color system.
 */
export const DEFAULT_COLOR_SHADE = 6;

/**
 * Ordered color palette for chart series - cycles for unlimited series.
 * Uses base color names; shade is applied via formatMantineColor().
 *
 * An explicit, ordered subset of {@link MantineThemeColors} (the SSOT).
 * `satisfies readonly MantineColor[]` compile-checks every entry against
 * the theme so the palette can never drift from the theme color set.
 */
export const SERIES_COLORS = [
  'blue',
  'green',
  'yellow',
  'red',
  'grape',
  'teal',
  'lime',
  'indigo',
  'orange',
  'pink',
  'cyan',
  'violet',
] as const satisfies readonly MantineColor[];

/**
 * Palette color for a series/element at a given index — cycles the
 * shared {@link SERIES_COLORS} palette, with the standard fallback.
 */
export function seriesColorAt(index: number): MantineColor {
  return SERIES_COLORS[index % SERIES_COLORS.length] ?? 'blue';
}

/**
 * Resolve a chart element's color: an explicit color wins; otherwise the
 * shared series palette is cycled by index. SSOT for every cartesian
 * element (series, bands, reference lines, reference areas) so an
 * uncolored element always gets a palette color rather than a flat default.
 */
export function chartElementColor(explicit: MantineColor | undefined, index: number): MantineColor {
  return explicit ?? seriesColorAt(index);
}

/**
 * Stable palette color for an arbitrary string key — hashes the key into
 * a {@link SERIES_COLORS} slot so the same value always renders the same
 * color, independent of order or the distinct-value set.
 */
export function seriesColorFor(key: string): MantineColor {
  const hash = Array.from(key).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) | 0, 7);
  return seriesColorAt(Math.abs(hash));
}

/**
 * Named Mantine shade indices for chart chrome. SSOT — keeps the bare
 * 0-9 shade numbers out of the rendering code.
 */
export const CHART_SHADE = {
  seriesFill: 5,
  seriesStroke: 4,
  marker: 9,
  ref: 4,
  axisChrome: 0,
  axisTitle: 1,
  gridLine: 5,
} as const;

/**
 * Safe shade band for auto-distinguishing same-colored series. Offsets wrap
 * within [min, max] so a duplicate color never lands on a washed-out extreme
 * (0-1) and never runs off the 0-9 Mantine range.
 */
export const SHADE_BAND = { min: 2, max: 9 } as const;

/**
 * Shade offset for the Nth usage of a resolved series color. The first usage
 * is unshifted; later usages spread +1, -1, +2, -2 around the base and then
 * repeat (period-5 cycle), so same-colored series stay visually distinct
 * without ever exceeding +/-2 shades.
 */
export function shadeOffsetForUsage(usageCount: number): number {
  const cycle = [0, 1, -1, 2, -2] as const;
  return cycle[usageCount % cycle.length] ?? 0;
}

/**
 * Apply a shade offset to a base shade, wrapping into {@link SHADE_BAND}
 * (never clamps, never leaves the safe band).
 */
export function shiftShade(base: number, offset: number): number {
  const size = SHADE_BAND.max - SHADE_BAND.min + 1;
  const wrapped = (((base + offset - SHADE_BAND.min) % size) + size) % size;
  return wrapped + SHADE_BAND.min;
}

/**
 * Per-series shade offsets keyed by resolved-color usage order: each series
 * gets the offset for how many EARLIER series resolved to the SAME color.
 * Disambiguates both explicitly-duplicated colors and palette wrap-around
 * (the 10th auto-colored series reusing the first hue).
 */
export function shadeOffsetsForColors(colors: readonly MantineColor[]): number[] {
  return colors.map((color, i) => shadeOffsetForUsage(colors.slice(0, i).filter((c) => c === color).length));
}

/**
 * Fill-opacity presets for chart areas and reference markers. SSOT —
 * keeps bare opacity literals out of the rendering code.
 */
export const fillOpacity = {
  light: 0.15,
  medium: 0.3,
  strong: 0.6,
  none: 0,
  band: 0.3,
  refArea: 0.4,
} as const;

/**
 * Formats a color name and optional shade into Mantine's color notation.
 *
 * @param color - Base color name (e.g., 'blue', 'red')
 * @param shade - Optional shade value 0-9 (default: DEFAULT_COLOR_SHADE)
 * @returns Formatted color string (e.g., 'blue.6')
 *
 * @example
 * formatMantineColor('blue')     // 'blue.6'
 * formatMantineColor('red', 3)   // 'red.3'
 */
export function formatMantineColor(color: string, shade: number = DEFAULT_COLOR_SHADE): string {
  return `${color}.${shade}`;
}

/**
 * Builds a Mantine CSS variable string for a color and shade. Use this
 * when you need the resolved CSS variable (e.g. for inline styles or
 * SVG fill/stroke). Use {@link formatMantineColor} when interpolating
 * into Mantine's `c=` / `bg=` props which accept the `name.shade` form.
 *
 * @example
 * mantineColorVar('blue')     // 'var(--mantine-color-blue-6)'
 * mantineColorVar('grape', 3) // 'var(--mantine-color-grape-3)'
 */
export function mantineColorVar(color: MantineColor, shade: number = DEFAULT_COLOR_SHADE): string {
  return `var(--mantine-color-${color}-${shade})`;
}
/**
 * Base Mantine color names for a boolean state. SSOT — keeps the bare
 * `'green'` / `'red'` literals out of badge and icon rendering code.
 */
export const BOOLEAN_COLOR = {
  true: 'green',
  false: 'red',
} as const;

/**
 * Mantine badge color for a boolean value: `true` → green, `false` → red.
 * Use for `<Badge color={...}>` and other plain color-name props.
 */
export function booleanBadgeColor(value: boolean): (typeof BOOLEAN_COLOR)[keyof typeof BOOLEAN_COLOR] {
  return value ? BOOLEAN_COLOR.true : BOOLEAN_COLOR.false;
}

/**
 * Shade indices for the boolean icon tone. SSOT for {@link booleanIconColor}
 * — the `true` and `false` states use slightly different shades for visual
 * balance against an icon glyph.
 */
export const BOOLEAN_ICON_SHADE = {
  true: 4,
  false: 5,
} as const;

/**
 * Mantine icon-tone color for a boolean value: `true` → `green.4`,
 * `false` → `red.5`. Use for `<ThemeIcon color={...}>` glyphs where a
 * shade-qualified tone reads better than the plain badge color.
 */
export function booleanIconColor(value: boolean): string {
  return value
    ? formatMantineColor(BOOLEAN_COLOR.true, BOOLEAN_ICON_SHADE.true)
    : formatMantineColor(BOOLEAN_COLOR.false, BOOLEAN_ICON_SHADE.false);
}
